import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Heart, Star, Target, Users, Coins, Check, X, Loader2, AlertTriangle,
  Wallet, ChevronDown, ChevronUp, Edit2, CheckCircle2, ArrowLeft,
  TrendingUp, DollarSign, Zap, Lock, Unlock, Bell, RefreshCw,
  PenSquare, Eye, Send, FileText, ChevronRight, Upload, Rocket, Code2,
  Sparkles,
} from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useBCHPrice } from '../hooks/useBCHPrice';
import { useBCHAddress } from '../hooks/useBCHAddress';
import { useTipWallet } from '../hooks/useTipWallet';
import { BCHLogo } from '../components/Layout';
import BCHTipButton from '../components/BCHTipButton';
import SupportSomeoneNew from '../components/SupportSomeoneNew';
import { getBCHPrice } from '../hooks/useBCHPrice';
import {
  MEMBERSHIP_KIND, MEMBERSHIP_D_TAG, MEMBERSHIP_TAG,
  GOAL_KIND, GOAL_D_TAG, GOAL_TAG, GOAL_PLEDGE_KIND, GOAL_PLEDGE_TAG,
  GOAL_ARCHIVE_TAG, goalArchiveDTag,
  SUPPORT_FEE_PERCENT, PLATFORM_FEE_ADDRESS,
  usdToSatsMembership, satsToDisplay, goalProgress, currentMonth,
  type MembershipData, type CreatorGoalData,
} from '../lib/creatorSupport';
import { uploadImage } from '../lib/imageUpload';
import BoostGoalModal from '../components/BoostGoalModal';
import EmbedBuilderModal from '../components/EmbedBuilderModal';
import CampaignComments from '../components/CampaignComments';
import { BOOST_KIND, GOAL_BOOST_TAG, type GoalBoostData } from '../lib/boost';
import { useVerifiedGoalBoosts } from '../hooks/useVerifiedGoalBoosts';

// ── Gated Post kind/tag constants ────────────────────────────────
// We use kind 30078 (app-specific addressable) with a special prefix
// so gated posts are discoverable per-creator but separate from other
// 30078 events (membership config, goals, etc.)
const GATED_POST_KIND = 30078;
const GATED_POST_TAG = 'bch-members-only';
const GATED_POST_D_PREFIX = 'bchnostr/gated/';

// Membership sub receipt kind — used for subscriber queries
const MEMBERSHIP_SUB_KIND = 9735;
const MEMBERSHIP_SUB_TAG = 'bch-membership-sub';

// ─────────────────────────────────────────────────────────────────
//  Hooks for reading membership & goal data from Nostr
// ─────────────────────────────────────────────────────────────────

export function useCreatorMembership(pubkey: string | undefined) {
  const { events } = useNostrQuery({
    filters: pubkey ? [{ kinds: [MEMBERSHIP_KIND], authors: [pubkey], '#t': [MEMBERSHIP_TAG], limit: 1 }] : [],
    enabled: !!pubkey,
    allRelays: true,
  });

  return useMemo(() => {
    if (!events.length) return null;
    // Pick the most recent
    const ev = [...events].sort((a, b) => b.created_at - a.created_at)[0];
    try {
      const data: MembershipData = JSON.parse(ev.content);
      return { event: ev, data };
    } catch { return null; }
  }, [events]);
}

export function useCreatorGoal(pubkey: string | undefined) {
  const { events } = useNostrQuery({
    filters: pubkey ? [{ kinds: [GOAL_KIND], authors: [pubkey], '#t': [GOAL_TAG], limit: 1 }] : [],
    enabled: !!pubkey,
    allRelays: true,
  });

  return useMemo(() => {
    if (!events.length) return null;
    const ev = [...events].sort((a, b) => b.created_at - a.created_at)[0];
    try {
      const data: CreatorGoalData = JSON.parse(ev.content);
      return { event: ev, data };
    } catch { return null; }
  }, [events]);
}

/**
 * Fetch a creator's ARCHIVED (completed) goals. Unlike the active goal, these
 * are addressable events with a unique d-tag per campaign, so they accumulate
 * and form the creator's funding history. Deduplicated by d-tag (keeping the
 * newest per campaign) and sorted newest-completed first.
 */
export function useCreatorGoalArchive(pubkey: string | undefined) {
  const { events, loading } = useNostrQuery({
    filters: pubkey
      ? [{ kinds: [GOAL_KIND], authors: [pubkey], '#t': [GOAL_ARCHIVE_TAG], limit: 100 }]
      : [],
    enabled: !!pubkey,
    allRelays: true,
  });

  return useMemo(() => {
    // One entry per campaign (d-tag); keep the most recent snapshot.
    const byD = new Map<string, typeof events[number]>();
    for (const ev of [...events].sort((a, b) => b.created_at - a.created_at)) {
      const d = ev.tags?.find((t: string[]) => t[0] === 'd')?.[1];
      if (!d) continue;
      if (!byD.has(d)) byD.set(d, ev);
    }
    const goals = [...byD.values()]
      .map((ev) => {
        try {
          const data: CreatorGoalData = JSON.parse(ev.content);
          return { event: ev, data };
        } catch { return null; }
      })
      .filter((g): g is { event: typeof events[number]; data: CreatorGoalData } => g !== null)
      .sort((a, b) => (b.data.completedAt ?? b.event.created_at) - (a.data.completedAt ?? a.event.created_at));
    return { goals, loading };
  }, [events, loading]);
}

/**
 * Fetch pledges for a creator goal.
 *
 * WHY THIS IS NOT JUST `#e: [goalEventId]`:
 * A creator goal is a REPLACEABLE addressable event (kind 30078, fixed
 * d-tag `bchnostr/creator-goal`). Every time the creator edits or re-saves
 * it, a brand-new event id is minted — but old pledges were tagged with the
 * OLD id via `['e', ...]`. Querying only the current id therefore drops
 * every pledge made before the last edit, which is exactly why the progress
 * bar showed up for some creators (never edited their goal) and not others
 * (edited it after receiving support).
 *
 * The stable identity of a campaign is the `createdAt` stored INSIDE the
 * goal content: it is preserved across edits (see the goal editor) and only
 * changes when a genuinely new goal is started. So we:
 *   1. query every pledge to this creator's goals (by `#p` + pledge tag),
 *   2. keep those whose `goal_created` tag matches this campaign, PLUS any
 *      legacy pledge (no `goal_created` tag) that points at the current
 *      goal event id — so old pledges made before this fix still count.
 *
 * `goalCreatedAt` is the campaign's stable id; pass goalData.createdAt.
 */
export function useGoalPledges(
  goalEventId: string | undefined,
  creatorPubkey?: string,
  goalCreatedAt?: number,
) {
  // Prefer the creator-scoped query (catches pledges across every version of
  // the goal). Fall back to the id-scoped query when we don't yet know the
  // creator, so nothing regresses for callers mid-refactor.
  const filters = creatorPubkey
    ? [{ kinds: [GOAL_PLEDGE_KIND], '#p': [creatorPubkey], '#t': [GOAL_PLEDGE_TAG], limit: 500 }]
    : goalEventId
      ? [{ kinds: [GOAL_PLEDGE_KIND], '#e': [goalEventId], '#t': [GOAL_PLEDGE_TAG], limit: 500 }]
      : [];

  const { events } = useNostrQuery({
    filters,
    enabled: filters.length > 0,
    allRelays: true,
  });

  return useMemo(() => {
    const campaignId = goalCreatedAt ? String(goalCreatedAt) : '';

    // Scope the raw events to THIS campaign.
    const scoped = events.filter((ev) => {
      const goalCreatedTag = ev.tags?.find((t: string[]) => t[0] === 'goal_created')?.[1];
      // Newer pledges carry the stable campaign id — match on it exactly.
      if (goalCreatedTag) return campaignId ? goalCreatedTag === campaignId : true;
      // Legacy pledge (no campaign tag): fall back to the goal event id it
      // referenced. This keeps pre-fix pledges counted for the current
      // version of the goal.
      if (goalEventId) {
        const eTag = ev.tags?.find((t: string[]) => t[0] === 'e')?.[1];
        return eTag === goalEventId;
      }
      return false;
    });

    // De-duplicate by event id (a pledge can arrive from several relays).
    const byId = new Map<string, typeof scoped[number]>();
    for (const ev of scoped) byId.set(ev.id, ev);
    const pledges = [...byId.values()];

    const total = pledges.reduce((sum, ev) => {
      const amt = ev.tags?.find((t: string[]) => t[0] === 'amount')?.[1];
      return sum + (amt ? parseInt(amt) || 0 : 0);
    }, 0);
    return { pledges, totalSats: total };
  }, [events, goalEventId, goalCreatedAt]);
}

/** Fetch all membership subscription receipts directed at a creator pubkey */
export function useSubscriptions(creatorPubkey: string | undefined) {
  const { events, loading } = useNostrQuery({
    filters: creatorPubkey ? [{
      kinds: [MEMBERSHIP_SUB_KIND],
      '#p': [creatorPubkey],
      '#t': [MEMBERSHIP_SUB_TAG],
      limit: 500,
    }] : [],
    enabled: !!creatorPubkey,
    allRelays: true,
  });

  return useMemo(() => {
    // Deduplicate: one payment per supporter per month (keep latest)
    const seen = new Map<string, any>(); // key = pubkey+month
    for (const ev of [...events].sort((a, b) => b.created_at - a.created_at)) {
      const month = ev.tags.find((t: string[]) => t[0] === 'month')?.[1] || '';
      const key = `${ev.pubkey}:${month}`;
      if (!seen.has(key)) seen.set(key, ev);
    }
    const subs = [...seen.values()];
    const thisMonth = currentMonth();
    const activeThisMonth = subs.filter(ev =>
      (ev.tags.find((t: string[]) => t[0] === 'month')?.[1] || '') === thisMonth
    );
    const allTimeSupporters = [...new Set(subs.map(ev => ev.pubkey))];
    return { subs, activeThisMonth, allTimeSupporters, loading };
  }, [events, loading]);
}

/** Check if a specific user has paid for the current month for a creator */
export function useIsActiveMember(creatorPubkey: string | undefined, supporterPubkey: string | undefined) {
  const thisMonth = currentMonth();
  // Look back 35 days to cover the current month generously
  const since = useMemo(() => {
    const d = new Date();
    d.setDate(1); // start of current month
    d.setHours(0, 0, 0, 0);
    return Math.floor(d.getTime() / 1000) - 86400; // one day buffer
  }, []);

  const { events } = useNostrQuery({
    filters: (creatorPubkey && supporterPubkey) ? [{
      kinds: [MEMBERSHIP_SUB_KIND],
      authors: [supporterPubkey],
      '#p': [creatorPubkey],
      '#t': [MEMBERSHIP_SUB_TAG],
      since,
      limit: 10,
    }] : [],
    enabled: !!(creatorPubkey && supporterPubkey),
    allRelays: true,
  });

  return useMemo(() => {
    const paidThisMonth = events.some(ev =>
      (ev.tags.find((t: string[]) => t[0] === 'month')?.[1] || '') === thisMonth
    );
    const lastPaidMonth = events.length > 0
      ? [...events].sort((a, b) => b.created_at - a.created_at)[0]
          .tags.find((t: string[]) => t[0] === 'month')?.[1] || null
      : null;
    return { paidThisMonth, lastPaidMonth };
  }, [events, thisMonth]);
}

/** Fetch gated posts published by a creator */
export function useGatedPosts(creatorPubkey: string | undefined) {
  const { events, loading } = useNostrQuery({
    filters: creatorPubkey ? [{
      kinds: [GATED_POST_KIND],
      authors: [creatorPubkey],
      '#t': [GATED_POST_TAG],
      limit: 50,
    }] : [],
    enabled: !!creatorPubkey,
    allRelays: true,
  });

  return useMemo(() => {
    const posts = [...events]
      .sort((a, b) => b.created_at - a.created_at)
      .map(ev => {
        try {
          const data = JSON.parse(ev.content);
          return { event: ev, data };
        } catch { return null; }
      })
      .filter(Boolean) as { event: any; data: { title: string; body: string; createdAt: number } }[];
    return { posts, loading };
  }, [events, loading]);
}

// ─────────────────────────────────────────────────────────────────
//  Main Page
// ─────────────────────────────────────────────────────────────────

export default function CreatorSupportPage() {
  const { user, login } = useAppContext();
  const { price: bchPrice } = useBCHPrice();
  const [searchParams] = useSearchParams();
  const creatorParam = searchParams.get('creator');

  // If viewing a specific creator, show their panel
  if (creatorParam) {
    return <CreatorSupportPanel pubkey={creatorParam} bchPrice={bchPrice} fullPage />;
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="px-4 py-5 border-b border-border">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Heart className="w-6 h-6 text-emerald-500" /> Creator Support
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Help your favourite creators with tips, memberships, and funding goals — all powered by Bitcoin Cash.
        </p>
      </div>

      {/* Own creator setup */}
      {user ? (
        <div className="px-4 py-5 border-b border-border space-y-4">
          <h2 className="font-bold text-base flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary" /> Your Creator Hub
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <MembershipSetupCard pubkey={user.pubkey} bchPrice={bchPrice} />
            <GoalSetupCard pubkey={user.pubkey} bchPrice={bchPrice} />
          </div>
        </div>
      ) : (
        <div className="px-4 py-8 text-center border-b border-border">
          <Heart className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-sm font-semibold mb-1">Log in to set up your creator profile</p>
          <p className="text-xs text-muted-foreground mb-4">Accept memberships and set funding goals</p>
          <button
            onClick={login}
            className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90"
          >
            Log In
          </button>
        </div>
      )}

      {/* Support Someone New */}
      <div className="px-4 py-4 border-b border-border">
        <SupportSomeoneNew />
      </div>

      {/* Browse all creators with goals */}
      <AllCreatorsWithGoals bchPrice={bchPrice} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  All creators with active goals
// ─────────────────────────────────────────────────────────────────

function AllCreatorsWithGoals({ bchPrice }: { bchPrice: number | null }) {
  const { events: goalEvents, loading } = useNostrQuery({
    filters: [{ kinds: [GOAL_KIND], '#t': [GOAL_TAG], limit: 100 }],
    enabled: true,
    allRelays: true,
  });

  // Active goal boosts (creatorPubkey → boost). Used to float boosted goals
  // to the top and render the "Boosted" badge.
  const [boostSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 2);
  const { events: goalBoostEvents } = useNostrQuery({
    filters: [{ kinds: [BOOST_KIND], '#t': [GOAL_BOOST_TAG], since: boostSince, limit: 100 }],
    enabled: true,
    allRelays: true,
  });
  // Only on-chain-verified boosts get the badge and top placement.
  const { verified: activeBoosts } = useVerifiedGoalBoosts(goalBoostEvents);

  const goals = useMemo(() => {
    const byPubkey = new Map<string, any>();
    for (const ev of goalEvents) {
      const existing = byPubkey.get(ev.pubkey);
      if (!existing || ev.created_at > existing.created_at) byPubkey.set(ev.pubkey, ev);
    }
    const list = [...byPubkey.values()]
      .map(ev => {
        try { return { event: ev, data: JSON.parse(ev.content) as CreatorGoalData }; }
        catch { return null; }
      })
      .filter(Boolean)
      .filter(g => g!.data.active) as { event: any; data: CreatorGoalData }[];

    // Boosted goals first (highest bid first), then the rest by recency.
    return list.sort((a, b) => {
      const ba = activeBoosts.get(a.event.pubkey);
      const bb = activeBoosts.get(b.event.pubkey);
      if (ba && bb) return bb.priceSats - ba.priceSats;
      if (ba) return -1;
      if (bb) return 1;
      return b.event.created_at - a.event.created_at;
    });
  }, [goalEvents, activeBoosts]);

  if (loading && goals.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (goals.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <Target className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">No active creator goals yet.</p>
        <p className="text-xs text-muted-foreground mt-1">Be the first to set a funding goal!</p>
      </div>
    );
  }

  return (
    <div className="px-4 py-4">
      <h2 className="font-bold text-base mb-3 flex items-center gap-2">
        <Target className="w-4 h-4 text-primary" /> Active Creator Goals
      </h2>
      <div className="space-y-3">
        {goals.map(g => (
          <GoalCard
            key={g.event.id}
            goalEvent={g.event}
            goalData={g.data}
            bchPrice={bchPrice}
            boost={activeBoosts.get(g.event.pubkey) ?? null}
          />
        ))}
      </div>
    </div>
  );
}

function GoalCard({ goalEvent, goalData, bchPrice, boost }: { goalEvent: any; goalData: CreatorGoalData; bchPrice: number | null; boost?: GoalBoostData | null }) {
  const { profile } = useNostrProfile(goalEvent.pubkey);
  const { pledges, totalSats } = useGoalPledges(goalEvent.id, goalEvent.pubkey, goalData.createdAt);
  const progress = goalProgress(totalSats, goalData.targetUsd, bchPrice);
  const targetSats = bchPrice ? usdToSatsMembership(goalData.targetUsd, bchPrice) : 0;
  const isBoosted = !!boost;

  // Count unique backers (excluding creator self-pledges)
  const backerCount = useMemo(() => {
    const pks = new Set<string>();
    for (const ev of pledges) {
      if (ev.pubkey !== goalEvent.pubkey) pks.add(ev.pubkey);
    }
    return pks.size;
  }, [pledges, goalEvent.pubkey]);

  return (
    <Link
      to={`/support?creator=${goalEvent.pubkey}`}
      className={`block rounded-2xl border bg-card transition-colors p-4 ${
        isBoosted
          ? 'border-amber-500/40 ring-1 ring-amber-500/30 animate-boost-glow'
          : 'border-border hover:border-primary/30'
      }`}
    >
      {isBoosted && (
        <div className="flex items-center gap-1.5 mb-2">
          <span className="boost-badge inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-white">
            <Rocket className="w-3 h-3" /> Boosted
          </span>
        </div>
      )}
      <div className="flex items-center gap-3 mb-3">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <span className="text-base">🎯</span>
          </div>
        )}
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{profile?.display_name || profile?.name || goalEvent.pubkey.slice(0, 12)}</p>
          <p className="text-xs text-muted-foreground truncate">{goalData.title}</p>
        </div>
        <span className="ml-auto text-xs font-bold text-primary shrink-0">{progress}%</span>
      </div>

      {/* Cover image */}
      {goalData.image && /^https?:\/\//i.test(goalData.image) && (
        <div className="mb-3 rounded-xl overflow-hidden bg-muted">
          <img src={goalData.image} alt="" loading="lazy" className="w-full max-h-40 object-cover block" />
        </div>
      )}

      {/* Progress bar */}
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="flex justify-between mt-1.5 text-[11px] text-muted-foreground">
        <span>{satsToDisplay(totalSats, bchPrice)} raised</span>
        <div className="flex items-center gap-2">
          {backerCount > 0 && (
            <span className="flex items-center gap-0.5 text-pink-500">
              <Heart className="w-3 h-3" /> {backerCount}
            </span>
          )}
          <span>Goal: ${goalData.targetUsd}</span>
        </div>
      </div>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────
//  Creator Support Panel — shown on profile page & /support?creator=
// ─────────────────────────────────────────────────────────────────

export function CreatorSupportPanel({
  pubkey,
  bchPrice,
  fullPage = false,
}: {
  pubkey: string;
  bchPrice: number | null;
  fullPage?: boolean;
}) {
  const { user } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useNostrProfile(pubkey);
  const isOwn = user?.pubkey === pubkey;
  const membership = useCreatorMembership(pubkey);
  const goal = useCreatorGoal(pubkey);
  const { pledges: goalPledges, totalSats: goalRaised } = useGoalPledges(
    goal?.event.id, pubkey, goal?.data.createdAt,
  );
  const { goals: pastGoals } = useCreatorGoalArchive(pubkey);

  // Back button: return to wherever the user came from (usually the Creator
  // Support goals list). React Router sets location.key to 'default' only on
  // a fresh page load, so a non-default key means the user reached this view
  // via in-app navigation and we can safely go back one step. If they opened
  // a shared /support?creator=… link directly, fall back to the Creator
  // Support home — NOT the profile page (the previous, incorrect behaviour).
  const handleBack = useCallback(() => {
    if (location.key && location.key !== 'default') navigate(-1);
    else navigate('/support');
  }, [navigate, location.key]);

  const hasMembership = !!membership?.data?.active;
  const hasGoal = !!goal?.data?.active;

  const { paidThisMonth, lastPaidMonth } = useIsActiveMember(
    hasMembership ? pubkey : undefined,
    !isOwn ? user?.pubkey : undefined,
  );

  // Nothing to show if no features set up and it's not the owner. A completed
  // funding history (past goals) is worth showing on its own, though.
  if (!hasMembership && !hasGoal && !isOwn && pastGoals.length === 0) return null;

  return (
    <div className={fullPage ? 'max-w-2xl mx-auto' : ''}>
      {fullPage && (
        <div className="px-4 py-4 border-b border-border flex items-center gap-3">
          <button onClick={handleBack} aria-label="Go back" className="p-2 rounded-xl hover:bg-muted transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            {profile?.picture && (
              <img src={profile.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
            )}
            <div>
              <p className="font-bold text-sm">{profile?.display_name || profile?.name || 'Creator'}</p>
              <p className="text-xs text-muted-foreground">Creator Support</p>
            </div>
          </div>
        </div>
      )}

      <div className={`space-y-3 ${fullPage ? 'p-4' : 'pt-3'}`}>
        {/* ── 💚 Tip ───────────────────────────────────────────── */}
        {!isOwn && (
          <TipCard pubkey={pubkey} profile={profile} />
        )}

        {/* ── ⭐ Membership ────────────────────────────────────── */}
        {hasMembership && (
          <MembershipCard
            pubkey={pubkey}
            membershipData={membership!.data}
            bchPrice={bchPrice}
            isOwn={isOwn}
            paidThisMonth={paidThisMonth}
            lastPaidMonth={lastPaidMonth}
          />
        )}
        {isOwn && !hasMembership && (
          <MembershipSetupCard pubkey={pubkey} bchPrice={bchPrice} />
        )}

        {/* ── 👥 Subscriber List (creator only) ────────────────── */}
        {isOwn && hasMembership && (
          <SubscriberList creatorPubkey={pubkey} />
        )}

        {/* ── 🔒 Gated Posts ───────────────────────────────────── */}
        {(isOwn || paidThisMonth) && hasMembership && (
          <GatedPostsSection
            creatorPubkey={pubkey}
            isOwn={isOwn}
            isMember={paidThisMonth}
          />
        )}
        {/* Teaser for non-members if creator has gated content */}
        {!isOwn && !paidThisMonth && hasMembership && (
          <GatedPostsTeaser creatorPubkey={pubkey} membershipData={membership!.data} />
        )}

        {/* ── 🎯 Creator Goal ──────────────────────────────────── */}
        {hasGoal && (
          <CreatorGoalCard
            pubkey={pubkey}
            goalEvent={goal!.event}
            goalData={goal!.data}
            raisedSats={goalRaised}
            pledges={goalPledges}
            bchPrice={bchPrice}
            isOwn={isOwn}
          />
        )}
        {isOwn && !hasGoal && (
          <GoalSetupCard pubkey={pubkey} bchPrice={bchPrice} />
        )}

        {/* ── 🏁 Past Goals (completed funding history) ────────── */}
        <PastGoalsSection pubkey={pubkey} bchPrice={bchPrice} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  💚 Tip Card
// ─────────────────────────────────────────────────────────────────

function TipCard({ pubkey, profile }: { pubkey: string; profile: any }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl">💚</span>
          <div>
            <p className="font-bold text-sm">Send a Tip</p>
            <p className="text-xs text-muted-foreground">One-time BCH support</p>
          </div>
        </div>
        <BCHTipButton
          recipientPubkey={pubkey}
          recipientProfile={profile}
          dropdownAlign="right"
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  ⭐ Membership Card (supporter view)
// ─────────────────────────────────────────────────────────────────

function MembershipCard({
  pubkey,
  membershipData,
  bchPrice,
  isOwn,
  paidThisMonth = false,
  lastPaidMonth = null,
}: {
  pubkey: string;
  membershipData: MembershipData;
  bchPrice: number | null;
  isOwn: boolean;
  paidThisMonth?: boolean;
  lastPaidMonth?: string | null;
}) {
  const { user } = useAppContext();
  const tipWallet = useTipWallet();
  const { publish } = useNostrPublish();
  const { address: creatorBCHAddress } = useBCHAddress(pubkey);
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [editing, setEditing] = useState(false);
  // Custom price: user can pick any amount >= creator's minimum
  const [selectedUsd, setSelectedUsd] = useState(membershipData.priceUsd);
  const [customUsd, setCustomUsd] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const activeUsd = showCustom ? (parseFloat(customUsd) || 0) : selectedUsd;
  const monthlySats = usdToSatsMembership(activeUsd, bchPrice);
  const minSats = usdToSatsMembership(membershipData.priceUsd, bchPrice);
  const monthStr = currentMonth();
  const canAfford = tipWallet.balanceSats >= BigInt(monthlySats) + BigInt(300);
  const meetsMinimum = activeUsd >= membershipData.priceUsd;

  // Price presets: creator's price + generous options above it
  // Adapts to any price range — from $0.10 to $500+
  const presets = useMemo(() => {
    const base = membershipData.priceUsd;
    const options: number[] = [base];

    // Generate meaningful multipliers above the base
    const multipliers = [2, 5, 10];
    for (const m of multipliers) {
      const val = Math.round(base * m * 100) / 100; // clean rounding
      if (val !== base && !options.includes(val)) options.push(val);
      if (options.length >= 4) break;
    }

    // If we still need more, add nice round numbers above the base
    if (options.length < 3) {
      const niceNumbers = [0.5, 1, 2, 3, 5, 10, 15, 20, 25, 50, 100, 200, 500];
      for (const n of niceNumbers) {
        if (n > base && !options.includes(n) && options.length < 4) options.push(n);
      }
    }

    return options.slice(0, 4); // max 4 presets to keep it clean
  }, [membershipData.priceUsd]);

  // Renewal reminder: they paid last month but not yet this month
  const prevMonth = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  const needsRenewal = !paidThisMonth && lastPaidMonth === prevMonth;

  const handleSupport = useCallback(async () => {
    if (!user || !bchPrice || state !== 'idle' || !meetsMinimum) return;
    setState('sending');
    try {
      if (!tipWallet.ready) await tipWallet.init();
      const sats = BigInt(monthlySats);
      if (tipWallet.balanceSats < sats + BigInt(300)) {
        setState('error');
        setErrorMsg('Insufficient wallet balance');
        return;
      }
      if (!creatorBCHAddress) {
        setState('error');
        setErrorMsg('Creator BCH address not found');
        return;
      }
      const txid = await tipWallet.sendTipWithFee(creatorBCHAddress, sats);
      await publish({
        kind: MEMBERSHIP_SUB_KIND,
        content: '',
        tags: [
          ['p', pubkey],
          ['t', MEMBERSHIP_SUB_TAG],
          ['month', monthStr],
          ['amount', monthlySats.toString()],
          ['txid', txid || ''],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setState('sent');
    } catch (e: any) {
      setState('error');
      setErrorMsg(e?.message || 'Payment failed');
    }
  }, [user, bchPrice, state, tipWallet, monthlySats, creatorBCHAddress, publish, pubkey, monthStr, meetsMinimum]);

  if (isOwn && editing) {
    return <MembershipEditor pubkey={pubkey} existing={membershipData} onDone={() => setEditing(false)} />;
  }

  return (
    <div className={`rounded-2xl border bg-card p-4 space-y-3 ${needsRenewal ? 'border-amber-500/40' : 'border-border'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xl">⭐</span>
          <div>
            <p className="font-bold text-sm">Membership</p>
            <p className="text-xs text-muted-foreground">From ${membershipData.priceUsd}/month · supporters get a badge</p>
          </div>
        </div>
        {isOwn && (
          <button onClick={() => setEditing(true)} className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground">
            <Edit2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {membershipData.description && (
        <p className="text-sm text-muted-foreground">{membershipData.description}</p>
      )}

      {!isOwn && user && (
        <div className="pt-1 space-y-2.5">
          {/* Active member badge */}
          {paidThisMonth && state !== 'sent' && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-semibold">
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
              You're a member for {monthStr} ✓
            </div>
          )}

          {/* Renewal reminder banner */}
          {needsRenewal && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400 text-xs font-medium">
              <Bell className="w-3.5 h-3.5 shrink-0" />
              Your membership expired — renew for {monthStr} to keep access to members-only content.
            </div>
          )}

          {state === 'sent' ? (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-sm font-semibold">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              You're a supporter for {monthStr}! 🎉
            </div>
          ) : !paidThisMonth ? (
            <div className="space-y-2.5">
              {/* Amount picker */}
              <div>
                <p className="text-[11px] font-semibold text-muted-foreground mb-1.5">Choose your amount</p>
                <div className="flex gap-1.5 flex-wrap">
                  {presets.map((p, i) => (
                    <button
                      key={p}
                      onClick={() => { setSelectedUsd(p); setShowCustom(false); }}
                      className={`px-3 py-2 rounded-xl text-xs font-bold transition-all active:scale-95 ${
                        !showCustom && selectedUsd === p
                          ? 'bg-amber-500 text-white shadow-md'
                          : 'bg-muted text-foreground hover:bg-muted/80'
                      }`}
                    >
                      ${p}{i === 0 ? '' : ''}
                    </button>
                  ))}
                  <button
                    onClick={() => { setShowCustom(true); setCustomUsd(''); }}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                      showCustom
                        ? 'bg-amber-500 text-white shadow-md'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    Custom
                  </button>
                </div>
                {presets.length > 1 && !showCustom && selectedUsd > membershipData.priceUsd && (
                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                    Extra generous — thank you!
                  </p>
                )}
              </div>

              {/* Custom input */}
              {showCustom && (
                <div className="flex items-center gap-2 bg-muted rounded-xl px-3 py-2 border border-amber-500/30 focus-within:border-amber-500/60 transition-colors">
                  <span className="text-sm font-bold text-muted-foreground">$</span>
                  <input
                    type="number"
                    min={membershipData.priceUsd}
                    step="0.5"
                    value={customUsd}
                    onChange={e => setCustomUsd(e.target.value)}
                    placeholder={`${membershipData.priceUsd} or more`}
                    autoFocus
                    className="flex-1 bg-transparent text-sm font-mono focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none placeholder:text-muted-foreground/40"
                  />
                </div>
              )}
              {showCustom && activeUsd > 0 && !meetsMinimum && (
                <p className="text-[10px] text-red-500">
                  Minimum membership price is ${membershipData.priceUsd}
                </p>
              )}

              {/* Pay button */}
              <button
                onClick={handleSupport}
                disabled={state === 'sending' || !meetsMinimum || activeUsd <= 0}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-500 text-white font-bold text-sm disabled:opacity-50 hover:bg-amber-600 active:scale-[0.98] transition-all"
              >
                {state === 'sending' ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</>
                ) : needsRenewal ? (
                  <><RefreshCw className="w-4 h-4" /> Renew for {monthStr} · ${activeUsd}</>
                ) : (
                  <>⭐ Become a Member · ${activeUsd}/mo</>
                )}
              </button>
            </div>
          ) : null}
          {state === 'error' && (
            <p className="text-xs text-red-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{errorMsg}</p>
          )}
        </div>
      )}

      {!user && !isOwn && (
        <p className="text-xs text-muted-foreground">Log in to become a member</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  ⭐ Membership Setup / Editor (creator own view)
// ─────────────────────────────────────────────────────────────────

function MembershipSetupCard({ pubkey, bchPrice }: { pubkey: string; bchPrice: number | null }) {
  const membership = useCreatorMembership(pubkey);
  const [editing, setEditing] = useState(false);
  // If membership data already exists, MembershipCard handles display — hide this card
  if (membership?.data) return null;
  // Once editing, show the full editor instead of the setup prompt
  if (editing) {
    return <MembershipEditor pubkey={pubkey} onDone={() => setEditing(false)} />;
  }
  return (
    <button
      onClick={() => setEditing(true)}
      className="w-full rounded-2xl border-2 border-dashed border-border hover:border-amber-500/40 bg-card p-4 flex items-center gap-3 text-left transition-colors group"
    >
      <span className="text-2xl">⭐</span>
      <div>
        <p className="font-semibold text-sm group-hover:text-foreground text-muted-foreground">Set up Membership</p>
        <p className="text-xs text-muted-foreground">Offer a monthly supporter tier</p>
      </div>
    </button>
  );
}

function MembershipEditor({
  pubkey,
  existing,
  onDone,
}: {
  pubkey: string;
  existing?: MembershipData;
  onDone: () => void;
}) {
  const { publish } = useNostrPublish();
  const { address: bchAddress } = useBCHAddress(pubkey);
  const [priceUsd, setPriceUsd] = useState(existing?.priceUsd?.toString() || '5');
  const [description, setDescription] = useState(existing?.description || '');
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const handleSave = async () => {
    const price = parseFloat(priceUsd);
    if (!price || price < 0.01) return;
    if (!bchAddress) return;
    setSaving(true);
    try {
      const data: MembershipData = {
        priceUsd: price,
        description: description.trim(),
        active: true,
        createdAt: Math.floor(Date.now() / 1000),
      };
      await publish({
        kind: MEMBERSHIP_KIND,
        content: JSON.stringify(data),
        tags: [
          ['d', MEMBERSHIP_D_TAG],
          ['t', MEMBERSHIP_TAG],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      onDone();
    } catch (e) {
      console.error('Membership save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!existing) return;
    setDeactivating(true);
    try {
      const data: MembershipData = { ...existing, active: false };
      await publish({
        kind: MEMBERSHIP_KIND,
        content: JSON.stringify(data),
        tags: [['d', MEMBERSHIP_D_TAG], ['t', MEMBERSHIP_TAG]],
        created_at: Math.floor(Date.now() / 1000),
      });
      onDone();
    } catch { } finally { setDeactivating(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-bold text-sm flex items-center gap-2"><span>⭐</span> {existing ? 'Edit Membership' : 'Set Up Membership'}</p>
        <button onClick={onDone} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"><X className="w-4 h-4" /></button>
      </div>

      {!bchAddress && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-700 dark:text-amber-400">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          Add a BCH address in Settings to receive membership payments.
        </div>
      )}

      <div className="space-y-2">
        <label className="text-xs font-semibold text-muted-foreground">Minimum Monthly Price (USD)</label>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-muted-foreground">$</span>
          <input
            type="number"
            min="0.01"
            step="any"
            value={priceUsd}
            onChange={e => setPriceUsd(e.target.value)}
            className="flex-1 bg-muted rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
            placeholder="5.00"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[0.25, 1, 5, 10, 25, 50, 100].map(p => (
            <button
              key={p}
              onClick={() => setPriceUsd(p.toString())}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                parseFloat(priceUsd) === p ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80 text-muted-foreground'
              }`}
            >
              {p < 1 ? `$${p}` : `$${p}`}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground">
          This is the minimum — supporters can pay more if they choose.
        </p>
      </div>

      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-muted-foreground">What do members get? (optional)</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={2}
          placeholder="e.g. Supporter badge, exclusive posts, early access…"
          className="w-full bg-muted rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground/50"
        />
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={handleSave}
          disabled={saving || !bchAddress || !priceUsd || parseFloat(priceUsd) < 0.01}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {existing ? 'Save Changes' : 'Activate Membership'}
        </button>
        {existing && (
          <button
            onClick={handleDeactivate}
            disabled={deactivating}
            className="px-3 py-2.5 rounded-xl border border-red-500/30 text-red-500 text-sm font-semibold hover:bg-red-500/10 disabled:opacity-50 transition-colors"
          >
            {deactivating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Deactivate'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  👥 Goal Backers — Social Proof
// ─────────────────────────────────────────────────────────────────

function BackerAvatar({ pubkey }: { pubkey: string }) {
  const { profile } = useNostrProfile(pubkey);
  return profile?.picture ? (
    <Link to={`/profile/${pubkey}`} title={profile?.display_name || profile?.name || pubkey.slice(0, 8)}>
      <img src={profile.picture} alt="" className="w-7 h-7 rounded-full object-cover ring-2 ring-card hover:ring-primary/40 transition-all" />
    </Link>
  ) : (
    <Link to={`/profile/${pubkey}`} title={pubkey.slice(0, 8)}>
      <div className="w-7 h-7 rounded-full bg-primary/10 ring-2 ring-card flex items-center justify-center">
        <Users className="w-3 h-3 text-primary/50" />
      </div>
    </Link>
  );
}

function BackerRow({ pubkey, sats, bchPrice }: { pubkey: string; sats: number; bchPrice: number | null }) {
  const { profile } = useNostrProfile(pubkey);
  const usd = bchPrice && sats > 0 ? (sats / 1e8) * bchPrice : 0;

  return (
    <Link to={`/profile/${pubkey}`} className="flex items-center gap-2 py-1.5 group">
      {profile?.picture ? (
        <img src={profile.picture} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Users className="w-3 h-3 text-primary/50" />
        </div>
      )}
      <span className="text-xs font-medium truncate flex-1 group-hover:text-primary transition-colors">
        {profile?.display_name || profile?.name || pubkey.slice(0, 8) + '…'}
      </span>
      <span className="text-[11px] font-mono text-muted-foreground tabular-nums shrink-0">
        {usd >= 0.01 ? `$${usd.toFixed(2)}` : sats >= 1000 ? `${(sats / 1000).toFixed(1)}k sats` : `${sats} sats`}
      </span>
    </Link>
  );
}

function GoalBackers({ pledges, creatorPubkey, bchPrice }: { pledges: any[]; creatorPubkey: string; bchPrice: number | null }) {
  const [expanded, setExpanded] = useState(false);

  // Aggregate pledges by backer, sum their amounts, skip self-pledges
  const backers = useMemo(() => {
    const byPk = new Map<string, { sats: number; latestAt: number }>();
    for (const ev of pledges) {
      if (ev.pubkey === creatorPubkey) continue; // skip self
      const amt = parseInt(ev.tags?.find((t: string[]) => t[0] === 'amount')?.[1] || '0') || 0;
      const existing = byPk.get(ev.pubkey);
      if (existing) {
        existing.sats += amt;
        existing.latestAt = Math.max(existing.latestAt, ev.created_at);
      } else {
        byPk.set(ev.pubkey, { sats: amt, latestAt: ev.created_at });
      }
    }
    return [...byPk.entries()]
      .map(([pk, data]) => ({ pubkey: pk, sats: data.sats, latestAt: data.latestAt }))
      .sort((a, b) => b.sats - a.sats); // top backers first
  }, [pledges, creatorPubkey]);

  if (backers.length === 0) return null;

  const showAll = expanded || backers.length <= 5;
  const visibleBackers = showAll ? backers : backers.slice(0, 5);

  return (
    <div className="border-t border-border px-4 py-3 space-y-2">
      {/* Compact avatar strip + count */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Heart className="w-3.5 h-3.5 text-pink-500" />
          <span className="text-xs font-semibold text-muted-foreground">
            {backers.length} {backers.length === 1 ? 'backer' : 'backers'}
          </span>
        </div>
        {/* Stacked avatars — top 5 backers */}
        <div className="flex items-center -space-x-2">
          {backers.slice(0, 6).map(b => (
            <BackerAvatar key={b.pubkey} pubkey={b.pubkey} />
          ))}
          {backers.length > 6 && (
            <div className="w-7 h-7 rounded-full bg-muted ring-2 ring-card flex items-center justify-center text-[10px] font-bold text-muted-foreground">
              +{backers.length - 6}
            </div>
          )}
        </div>
      </div>

      {/* Expanded list with amounts */}
      {backers.length > 0 && (
        <div>
          {visibleBackers.map(b => (
            <BackerRow key={b.pubkey} pubkey={b.pubkey} sats={b.sats} bchPrice={bchPrice} />
          ))}
          {backers.length > 5 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-[11px] text-primary font-medium mt-1 hover:underline"
            >
              {expanded ? (
                <><ChevronUp className="w-3 h-3" /> Show less</>
              ) : (
                <><ChevronDown className="w-3 h-3" /> Show all {backers.length} backers</>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  🎯 Creator Goal Card (supporter view)
// ─────────────────────────────────────────────────────────────────

function CreatorGoalCard({
  pubkey,
  goalEvent,
  goalData,
  raisedSats,
  pledges,
  bchPrice,
  isOwn,
}: {
  pubkey: string;
  goalEvent: any;
  goalData: CreatorGoalData;
  raisedSats: number;
  pledges: any[];
  bchPrice: number | null;
  isOwn: boolean;
}) {
  const { user } = useAppContext();
  const tipWallet = useTipWallet();
  const { publish } = useNostrPublish();
  const { address: creatorBCHAddress } = useBCHAddress(pubkey);
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [customUsd, setCustomUsd] = useState('');
  const [editing, setEditing] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);
  const [showBoost, setShowBoost] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);
  // Owner opt-in: automatically archive & finish a goal once it reaches 100%.
  const [autoComplete, setAutoComplete] = useState<boolean>(() => {
    try { return localStorage.getItem('bchnostr:goal-auto-complete') === '1'; }
    catch { return false; }
  });

  const progress = goalProgress(raisedSats, goalData.targetUsd, bchPrice);
  const targetSats = bchPrice ? usdToSatsMembership(goalData.targetUsd, bchPrice) : 0;
  const isComplete = progress >= 100;

  // Is this goal currently boosted? (Fetched independently so the panel shows
  // the badge whether you arrived via profile or the goals list.)
  const [boostSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 2);
  const { events: goalBoostEvents } = useNostrQuery({
    filters: [{ kinds: [BOOST_KIND], '#t': [GOAL_BOOST_TAG], since: boostSince, limit: 100 }],
    enabled: true,
    allRelays: true,
  });
  // Only trust boosts whose BCH payment is confirmed on-chain.
  const { verified: verifiedBoosts } = useVerifiedGoalBoosts(goalBoostEvents);
  const activeBoost = verifiedBoosts.get(pubkey) ?? null;

  const handlePledge = useCallback(async (usdAmount: number) => {
    if (!user || !bchPrice || state !== 'idle') return;
    setState('sending');
    try {
      if (!tipWallet.ready) await tipWallet.init();
      const sats = BigInt(Math.round((usdAmount / bchPrice) * 1e8));
      if (tipWallet.balanceSats < sats + BigInt(300)) {
        setState('error'); setErrorMsg('Insufficient balance'); return;
      }
      if (!creatorBCHAddress) {
        setState('error'); setErrorMsg('Creator BCH address not found'); return;
      }
      const txid = await tipWallet.sendTipWithFee(creatorBCHAddress, sats);
      await publish({
        kind: GOAL_PLEDGE_KIND,
        content: '',
        tags: [
          ['e', goalEvent.id],
          ['p', pubkey],
          ['t', GOAL_PLEDGE_TAG],
          // Stable campaign id — survives goal edits so this pledge keeps
          // counting toward the same goal even after the creator re-saves it.
          ['goal_created', String(goalData.createdAt)],
          ['amount', Number(sats).toString()],
          ['txid', txid || ''],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setState('sent');
    } catch (e: any) {
      setState('error'); setErrorMsg(e?.message || 'Payment failed');
    }
  }, [user, bchPrice, state, tipWallet, creatorBCHAddress, publish, pubkey, goalEvent.id]);

  const handleComplete = useCallback(async () => {
    if (!isOwn) return;
    // Guard against double-firing (manual click + auto-complete effect).
    if (completing || goalData.completedAt) return;
    setCompleting(true);
    try {
      const now = Math.floor(Date.now() / 1000);
      const updated: CreatorGoalData = { ...goalData, active: false, completedAt: now };

      // 1. Snapshot the finished goal into its own addressable event so it is
      //    preserved once the active (replaceable) slot is reused for a new
      //    goal. The d-tag is derived from the goal's stable createdAt, so the
      //    same campaign always maps to the same archive event (idempotent),
      //    and pledges (keyed by `goal_created`) keep resolving their totals.
      await publish({
        kind: GOAL_KIND,
        content: JSON.stringify(updated),
        tags: [
          ['d', goalArchiveDTag(goalData.createdAt)],
          ['t', GOAL_ARCHIVE_TAG],
          // Keep the campaign's stable id on the archive event too, so the
          // Past Goals list can match pledges without re-parsing content.
          ['goal_created', String(goalData.createdAt)],
        ],
        created_at: now,
      });

      // 2. Mark the active goal complete (still replaceable — will be replaced
      //    when the creator starts a new goal).
      await publish({
        kind: GOAL_KIND,
        content: JSON.stringify(updated),
        tags: [['d', GOAL_D_TAG], ['t', GOAL_TAG]],
        created_at: now,
      });
    } catch { } finally { setCompleting(false); }
  }, [isOwn, completing, goalData, publish]);

  // Opt-in auto-complete: when the owner has this on and their own goal has
  // reached 100%, archive & finish it automatically. Runs only in the owner's
  // own session (publishing needs their signer) and only once per mount, after
  // a short settle delay so a transient BCH-price blip past 100% doesn't fire
  // it prematurely. `handleComplete` itself is idempotent and guards on
  // `completedAt`, so a stale/duplicate trigger is harmless.
  const autoFiredRef = useRef(false);
  useEffect(() => {
    if (!autoComplete) return;
    if (!isOwn) return;
    if (!isComplete) { autoFiredRef.current = false; return; }
    if (goalData.completedAt || completing || autoFiredRef.current) return;
    autoFiredRef.current = true;
    const t = setTimeout(() => { void handleComplete(); }, 4000);
    return () => clearTimeout(t);
  }, [autoComplete, isOwn, isComplete, goalData.completedAt, completing, handleComplete]);

  const toggleAutoComplete = useCallback(() => {
    setAutoComplete(prev => {
      const next = !prev;
      try { localStorage.setItem('bchnostr:goal-auto-complete', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const handleShare = async () => {
    if (!user || sharing) return;
    setSharing(true);
    try {
      const npub = nip19.npubEncode(pubkey);
      const supportUrl = `https://bchnostr.com/support?creator=${pubkey}`;
      const progressText = isComplete ? '✅ Goal reached!' : `${progress}% funded`;
      const text = [
        `🎯 Help reach this BCH creator goal: "${goalData.title}"`,
        '',
        goalData.description ? goalData.description : '',
        `\uD83D\uDCCA ${progressText} · $${goalData.targetUsd} target`,
        '',
        `Support nostr:${npub} here \uD83D\uDC47`,
        supportUrl,
      ].filter(Boolean).join('\n').replace(/\n{3,}/g, '\n\n').trim();

      await publish({
        kind: 1,
        content: text,
        tags: [
          ['p', pubkey],
          ['t', 'bch'],
          ['t', 'bitcoincash'],
          ['t', 'bchnostr'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setShared(true);
      setTimeout(() => setShared(false), 3000);
    } catch (e) {
      console.error('Share failed:', e);
    } finally {
      setSharing(false);
    }
  };

  if (editing) return <GoalEditor pubkey={pubkey} existing={goalData} bchPrice={bchPrice} onDone={() => setEditing(false)} />;

  const presets = [1, 5, 10, 25];
  const parsedCustom = parseFloat(customUsd);
  const customValid = !isNaN(parsedCustom) && parsedCustom >= 0.5;

  return (
    <div className={`rounded-2xl border bg-card overflow-hidden ${
      activeBoost ? 'border-amber-500/40 ring-1 ring-amber-500/30' : 'border-border'
    }`}>
      {activeBoost && (
        <div className="px-4 pt-3 -mb-1">
          <span className="boost-badge inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-white">
            <Rocket className="w-3 h-3" /> Boosted goal
          </span>
        </div>
      )}
      {/* Header band */}
      <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 text-lg">🎯</div>
          <div>
            <p className="font-bold text-base leading-tight">{goalData.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Creator Goal · <span className="font-semibold">${goalData.targetUsd}</span> target</p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {/* Boost — draw attention to this goal (anyone logged in) */}
          {user && (
            <button
              onClick={() => setShowBoost(true)}
              title="Boost this goal"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 transition-colors"
            >
              <Rocket className="w-3.5 h-3.5" /> Boost
            </button>
          )}
          {/* Share to community feed — available to everyone logged in */}
          {user && (
            <button
              onClick={handleShare}
              disabled={sharing}
              title="Share to BCH community feed"
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                shared
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                  : 'hover:bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {sharing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : shared ? (
                <><Check className="w-3.5 h-3.5" /> Shared!</>
              ) : (
                <><Send className="w-3.5 h-3.5" /> Share</>
              )}
            </button>
          )}
          {/* Embed — put a live donate widget on any website */}
          <button
            onClick={() => setShowEmbed(true)}
            title="Embed this goal on a website"
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-primary hover:bg-primary/10 transition-colors"
          >
            <Code2 className="w-3.5 h-3.5" /> Embed
          </button>
          {isOwn && (
            <button onClick={() => setEditing(true)} className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground">
              <Edit2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {showEmbed && (
        <EmbedBuilderModal creatorPubkey={pubkey} onClose={() => setShowEmbed(false)} />
      )}

      {/* Cover image */}
      {goalData.image && /^https?:\/\//i.test(goalData.image) && (
        <div className="px-4 pb-3">
          <div className="rounded-xl overflow-hidden bg-muted">
            <img src={goalData.image} alt="" loading="lazy" className="w-full max-h-64 object-cover block" />
          </div>
        </div>
      )}

      {/* Description */}
      {goalData.description && (
        <div className="px-4 pb-3">
          <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap bg-muted/40 rounded-xl px-3 py-2.5">
            {goalData.description}
          </p>
        </div>
      )}

      {/* Progress */}
      <div className="px-4 pb-4 space-y-2">
        <div className="h-3 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${isComplete ? 'bg-emerald-500' : 'bg-primary'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-sm font-semibold ${isComplete ? 'text-emerald-500' : 'text-foreground'}`}>
            {isComplete ? '✅ Goal reached!' : `${satsToDisplay(raisedSats, bchPrice)} raised`}
          </span>
          <span className={`text-sm font-bold tabular-nums ${isComplete ? 'text-emerald-500' : 'text-primary'}`}>
            {progress}%
            <span className="text-xs font-normal text-muted-foreground ml-1">of ${goalData.targetUsd}</span>
          </span>
        </div>
      </div>

      {/* Backers — social proof */}
      {pledges.length > 0 && <GoalBackers pledges={pledges} creatorPubkey={pubkey} bchPrice={bchPrice} />}

      {/* Support actions */}
      {!isOwn && user && state !== 'sent' && !isComplete && (
        <div className="border-t border-border px-4 py-3 space-y-2.5">
          <p className="text-xs font-semibold text-muted-foreground">Choose an amount to contribute</p>
          <div className="grid grid-cols-4 gap-1.5">
            {presets.map(p => (
              <button
                key={p}
                onClick={() => handlePledge(p)}
                disabled={state === 'sending'}
                className="py-2.5 rounded-xl bg-muted hover:bg-primary/10 hover:text-primary text-sm font-bold transition-colors disabled:opacity-40"
              >
                ${p}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <div className="flex items-center flex-1 gap-1.5 bg-muted rounded-xl px-3 border border-border/50 focus-within:border-primary/40 focus-within:ring-1 focus-within:ring-primary/20 transition-all">
              <span className="text-sm text-muted-foreground font-bold">$</span>
              <input
                type="number" min="0.5" step="0.5"
                placeholder="Custom amount"
                value={customUsd}
                onChange={e => setCustomUsd(e.target.value)}
                className="flex-1 bg-transparent py-2.5 text-sm font-mono focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none placeholder:text-muted-foreground/40"
              />
            </div>
            <button
              onClick={() => customValid && handlePledge(parsedCustom)}
              disabled={!customValid || state === 'sending'}
              className="px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-bold disabled:opacity-40 hover:bg-primary/90 transition-colors"
            >
              {state === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : '🎯 Support'}
            </button>
          </div>
          {state === 'error' && (
            <p className="text-xs text-red-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{errorMsg}</p>
          )}
        </div>
      )}

      {state === 'sent' && (
        <div className="border-t border-border px-4 py-3 flex items-center gap-2 text-emerald-600 dark:text-emerald-400 text-sm font-semibold">
          <CheckCircle2 className="w-4 h-4 shrink-0" /> Thank you for supporting this goal! 🎉
        </div>
      )}

      {!user && !isOwn && (
        <div className="border-t border-border px-4 py-3">
          <p className="text-xs text-muted-foreground">Log in to support this goal</p>
        </div>
      )}

      {/* Owner complete control — celebratory reminder once the goal is reached */}
      {isOwn && isComplete && !goalData.completedAt && (
        <div className="border-t border-border bg-emerald-500/10 px-4 py-3.5 space-y-3">
          <div className="flex items-start gap-2.5">
            <Sparkles className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
                🎉 Goal reached — nice work!
              </p>
              <p className="text-xs text-emerald-700/80 dark:text-emerald-300/80 mt-0.5">
                {autoComplete
                  ? 'Auto-finishing this goal and saving it to your Past Goals…'
                  : 'Mark it complete to archive it in your Past Goals and start a fresh one.'}
              </p>
            </div>
          </div>

          <button
            onClick={handleComplete}
            disabled={completing}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-500 text-white font-bold text-sm hover:bg-emerald-600 disabled:opacity-50 transition-colors"
          >
            {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {completing ? 'Finishing…' : 'Mark Goal Complete & Create New One'}
          </button>

          <label className="flex items-center gap-2 text-xs text-emerald-800/90 dark:text-emerald-200/90 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoComplete}
              onChange={toggleAutoComplete}
              className="rounded border-emerald-500/50 text-emerald-500 focus:ring-emerald-500/40"
            />
            Automatically finish future goals as soon as they’re reached
          </label>
        </div>
      )}

      {/* When auto-complete is on but the goal isn't reached yet, reassure the
          owner it will be handled — no reminder needed. */}
      {isOwn && !isComplete && !goalData.completedAt && autoComplete && (
        <div className="border-t border-border px-4 py-2.5">
          <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            Auto-complete is on — this goal will finish itself when it hits 100%.
            <button onClick={toggleAutoComplete} className="underline hover:text-foreground no-navigate">Turn off</button>
          </p>
        </div>
      )}

      {/* Questions & Comments — supporters can ask about the campaign here */}
      <CampaignComments goalEvent={goalEvent} creatorPubkey={pubkey} campaignId={goalData.createdAt} />

      {showBoost && (
        <BoostGoalModal
          creatorPubkey={pubkey}
          goalTitle={goalData.title}
          onClose={() => setShowBoost(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  🏁 Past Goals — a creator's completed funding history
// ─────────────────────────────────────────────────────────────────

/** A single completed goal, read-only, with its final raised total. */
function PastGoalCard({
  pubkey,
  goalEvent,
  goalData,
  bchPrice,
}: {
  pubkey: string;
  goalEvent: any;
  goalData: CreatorGoalData;
  bchPrice: number | null;
}) {
  const { pledges, totalSats } = useGoalPledges(goalEvent.id, pubkey, goalData.createdAt);
  const progress = goalProgress(totalSats, goalData.targetUsd, bchPrice);
  const reached = progress >= 100;
  const completedDate = goalData.completedAt
    ? new Date(goalData.completedAt * 1000).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    : null;

  return (
    <div className="rounded-xl border border-border bg-card/60 overflow-hidden">
      <div className="px-3.5 py-3">
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold text-sm text-foreground leading-snug">{goalData.title}</p>
          <span
            className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
              reached
                ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {reached ? <><CheckCircle2 className="w-3 h-3" /> Reached</> : 'Ended'}
          </span>
        </div>

        {completedDate && (
          <p className="text-[11px] text-muted-foreground mt-0.5">Completed {completedDate}</p>
        )}

        {goalData.description && (
          <p className="text-xs text-muted-foreground mt-2 line-clamp-2 whitespace-pre-wrap">
            {goalData.description}
          </p>
        )}

        {/* Final progress bar */}
        <div className="mt-3 space-y-1.5">
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${reached ? 'bg-emerald-500' : 'bg-primary/70'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between items-center text-[11px]">
            <span className="font-medium text-foreground/80">
              {satsToDisplay(totalSats, bchPrice)} raised
            </span>
            <span className="tabular-nums font-semibold text-muted-foreground">
              {progress}% <span className="font-normal">of ${goalData.targetUsd}</span>
            </span>
          </div>
        </div>

        {pledges.length > 0 && (
          <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1">
            <Users className="w-3 h-3" />
            {pledges.length} {pledges.length === 1 ? 'backer' : 'backers'}
          </p>
        )}
      </div>

      {/* Q&A thread — continuous with the campaign's active-phase comments */}
      <CampaignComments goalEvent={goalEvent} creatorPubkey={pubkey} campaignId={goalData.createdAt} />
    </div>
  );
}

/** Collapsible list of a creator's completed goals. */
function PastGoalsSection({ pubkey, bchPrice }: { pubkey: string; bchPrice: number | null }) {
  const { goals, loading } = useCreatorGoalArchive(pubkey);
  const [open, setOpen] = useState(false);

  if (loading && goals.length === 0) return null;
  if (goals.length === 0) return null;

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
      >
        <span className="flex items-center gap-2 font-semibold text-sm">
          <span className="text-lg">🏁</span>
          Past Goals
          <span className="text-xs font-normal text-muted-foreground">({goals.length})</span>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2.5">
          {goals.map(g => (
            <PastGoalCard
              key={g.event.id}
              pubkey={pubkey}
              goalEvent={g.event}
              goalData={g.data}
              bchPrice={bchPrice}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  🎯 Goal Setup / Editor (creator own view)
// ─────────────────────────────────────────────────────────────────

function GoalSetupCard({ pubkey, bchPrice }: { pubkey: string; bchPrice: number | null }) {
  const goal = useCreatorGoal(pubkey);
  const [editing, setEditing] = useState(false);

  if (goal?.data?.active && !editing) return null;
  if (editing) return <GoalEditor pubkey={pubkey} bchPrice={bchPrice} onDone={() => setEditing(false)} />;

  return (
    <button
      onClick={() => setEditing(true)}
      className="w-full rounded-2xl border-2 border-dashed border-border hover:border-primary/40 bg-card p-4 flex items-center gap-3 text-left transition-colors group"
    >
      <span className="text-2xl">🎯</span>
      <div>
        <p className="font-semibold text-sm group-hover:text-foreground text-muted-foreground">
          {goal ? 'Set a New Goal' : 'Set a Creator Goal'}
        </p>
        <p className="text-xs text-muted-foreground">One active funding goal at a time</p>
      </div>
    </button>
  );
}

function GoalEditor({
  pubkey,
  existing,
  bchPrice,
  onDone,
}: {
  pubkey: string;
  existing?: CreatorGoalData;
  bchPrice: number | null;
  onDone: () => void;
}) {
  const { publish } = useNostrPublish();
  const { address: bchAddress } = useBCHAddress(pubkey);
  const [title, setTitle] = useState(existing?.title || '');
  const [description, setDescription] = useState(existing?.description || '');
  const [targetUsd, setTargetUsd] = useState(existing?.targetUsd?.toString() || '');
  const [image, setImage] = useState(existing?.image || '');
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const imageFileRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const handleSave = async () => {
    const target = parseFloat(targetUsd);
    if (!title.trim() || !target || target < 1 || !bchAddress) return;
    setSaving(true);
    try {
      const cleanImage = image.trim();
      const data: CreatorGoalData = {
        title: title.trim(),
        description: description.trim(),
        targetUsd: target,
        active: true,
        createdAt: existing?.createdAt || Math.floor(Date.now() / 1000),
        // Only persist safe https(s) image URLs.
        ...(/^https?:\/\//i.test(cleanImage) ? { image: cleanImage } : {}),
      };
      await publish({
        kind: GOAL_KIND,
        content: JSON.stringify(data),
        tags: [['d', GOAL_D_TAG], ['t', GOAL_TAG]],
        created_at: Math.floor(Date.now() / 1000),
      });
      onDone();
    } catch (e) { console.error(e); } finally { setSaving(false); }
  };

  const handleDeactivate = async () => {
    if (!existing) return;
    setDeactivating(true);
    try {
      const data: CreatorGoalData = { ...existing, active: false };
      await publish({
        kind: GOAL_KIND,
        content: JSON.stringify(data),
        tags: [['d', GOAL_D_TAG], ['t', GOAL_TAG]],
        created_at: Math.floor(Date.now() / 1000),
      });
      onDone();
    } catch { } finally { setDeactivating(false); }
  };

  const targetSats = bchPrice && targetUsd ? usdToSatsMembership(parseFloat(targetUsd) || 0, bchPrice) : 0;

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <p className="font-bold text-sm flex items-center gap-2">
          <span>🎯</span> {existing ? 'Edit Goal' : 'New Creator Goal'}
        </p>
        <button onClick={onDone} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {!bchAddress && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            Add a BCH address in Settings to receive goal payments.
          </div>
        )}

        {/* Title */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Goal Title</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. New recording equipment"
            className="w-full bg-background border border-border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 placeholder:text-muted-foreground/40 transition-all"
          />
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Description <span className="normal-case font-normal">(optional)</span>
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={4}
            placeholder="Tell supporters what you'll use the funds for and why it matters…"
            className="w-full bg-background border border-border rounded-xl px-3 py-2.5 text-sm leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 placeholder:text-muted-foreground/40 transition-all"
          />
        </div>

        {/* Cover image (optional) */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Cover Image <span className="normal-case font-normal">(optional)</span>
          </label>

          <input
            type="file"
            ref={imageFileRef}
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              e.target.value = '';
              setImageError(null);
              setImageUploading(true);
              try {
                const url = await uploadImage(file);
                setImage(url);
              } catch {
                setImageError('Image upload failed. Try a smaller image or paste a URL instead.');
              } finally {
                setImageUploading(false);
              }
            }}
          />

          {image ? (
            <div className="relative rounded-xl overflow-hidden border border-border bg-muted">
              {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
              <img src={image} alt="Goal cover" className="w-full max-h-48 object-cover block" />
              <button
                type="button"
                onClick={() => setImage('')}
                className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/60 text-white hover:bg-black/80 transition-colors"
                title="Remove image"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => imageFileRef.current?.click()}
                disabled={imageUploading}
                className="w-full flex items-center justify-center gap-2 py-6 rounded-xl border-2 border-dashed border-border hover:border-primary/40 transition-colors cursor-pointer disabled:opacity-60"
              >
                {imageUploading ? (
                  <><Loader2 className="w-5 h-5 animate-spin text-primary" /> <span className="text-xs text-muted-foreground">Uploading…</span></>
                ) : (
                  <><Upload className="w-5 h-5 text-muted-foreground" /> <span className="text-xs text-muted-foreground">Upload an image</span></>
                )}
              </button>
              <div>
                <p className="text-[10px] text-muted-foreground mb-1">Or paste an image URL:</p>
                <input
                  type="url"
                  onChange={e => {
                    const url = e.target.value.trim();
                    if (/^https?:\/\//i.test(url)) setImage(url);
                  }}
                  onPaste={e => {
                    const url = e.clipboardData.getData('text').trim();
                    if (/^https?:\/\//i.test(url)) { e.preventDefault(); setImage(url); }
                  }}
                  placeholder="https://example.com/image.jpg"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                />
              </div>
            </>
          )}
          {imageError && (
            <p className="text-[11px] text-red-500">{imageError}</p>
          )}
        </div>

        {/* Target amount */}
        <div className="space-y-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Target Amount (USD)</label>
          <div className="flex items-center gap-0 bg-background border border-border rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary/40 transition-all">
            <span className="pl-3 pr-1 text-sm font-bold text-muted-foreground">$</span>
            <input
              type="number" min="1" step="1"
              value={targetUsd}
              onChange={e => setTargetUsd(e.target.value)}
              placeholder="100"
              className="flex-1 bg-transparent px-2 py-2.5 text-sm font-mono focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
          {targetSats > 0 && bchPrice && (
            <p className="text-[11px] text-muted-foreground pl-1">≈ {targetSats.toLocaleString()} sats at current BCH price</p>
          )}
          <div className="flex gap-1.5 flex-wrap">
            {[50, 100, 250, 500, 1000].map(t => (
              <button
                key={t}
                onClick={() => setTargetUsd(t.toString())}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  targetUsd === t.toString()
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                }`}
              >
                ${t}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={handleSave}
            disabled={saving || !bchAddress || !title.trim() || !targetUsd || parseFloat(targetUsd) < 1}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {existing ? 'Save Goal' : 'Publish Goal'}
          </button>
          {existing && (
            <button
              onClick={handleDeactivate}
              disabled={deactivating}
              className="px-3 py-2.5 rounded-xl border border-red-500/30 text-red-500 text-sm font-semibold hover:bg-red-500/10 disabled:opacity-50 transition-colors"
            >
              {deactivating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Remove'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  👥 Subscriber List (creator-only view)
// ─────────────────────────────────────────────────────────────────

function SubscriberList({ creatorPubkey }: { creatorPubkey: string }) {
  const { subs, activeThisMonth, allTimeSupporters, loading } = useSubscriptions(creatorPubkey);
  const [expanded, setExpanded] = useState(false);
  const thisMonth = currentMonth();

  if (loading && subs.length === 0) return null;

  return (
    <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between gap-2"
      >
        <div className="flex items-center gap-2">
          <span className="text-xl">👥</span>
          <div className="text-left">
            <p className="font-bold text-sm">Subscribers</p>
            <p className="text-xs text-muted-foreground">
              {activeThisMonth.length} active this month · {allTimeSupporters.length} all-time
            </p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="space-y-2 pt-1">
          {/* This month */}
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Active — {thisMonth}</p>
          {activeThisMonth.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">No payments yet this month.</p>
          ) : (
            <div className="space-y-1.5">
              {activeThisMonth.map(ev => (
                <SubscriberRow key={ev.id} pubkey={ev.pubkey} amountSats={parseInt(ev.tags.find((t: string[]) => t[0] === 'amount')?.[1] || '0')} month={ev.tags.find((t: string[]) => t[0] === 'month')?.[1] || ''} />
              ))}
            </div>
          )}

          {/* Previous months — deduplicated unique supporters */}
          {allTimeSupporters.length > activeThisMonth.length && (
            <>
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide pt-2">Previous Supporters</p>
              <div className="space-y-1.5">
                {allTimeSupporters
                  .filter(pk => !activeThisMonth.some(ev => ev.pubkey === pk))
                  .map(pk => (
                    <SubscriberRow key={pk} pubkey={pk} lapsed />
                  ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SubscriberRow({ pubkey, amountSats, month, lapsed }: {
  pubkey: string; amountSats?: number; month?: string; lapsed?: boolean;
}) {
  const { profile } = useNostrProfile(pubkey);
  const npub = useMemo(() => { try { return nip19.npubEncode(pubkey); } catch { return pubkey; } }, [pubkey]);
  const name = profile?.display_name || profile?.name || npub.slice(0, 12) + '…';

  return (
    <Link
      to={`/profile/${pubkey}`}
      className="flex items-center gap-2.5 px-2 py-1.5 rounded-xl hover:bg-muted transition-colors"
    >
      {profile?.picture ? (
        <img src={profile.picture} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <span className="text-xs">👤</span>
        </div>
      )}
      <span className="text-sm font-medium flex-1 truncate">{name}</span>
      {lapsed ? (
        <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">lapsed</span>
      ) : (
        month && <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">{month}</span>
      )}
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────
//  🔒 Gated Posts — creator composer + member feed
// ─────────────────────────────────────────────────────────────────

function GatedPostsSection({ creatorPubkey, isOwn, isMember }: {
  creatorPubkey: string; isOwn: boolean; isMember: boolean;
}) {
  const { posts, loading } = useGatedPosts(creatorPubkey);
  const [composing, setComposing] = useState(false);

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-amber-500" />
          <p className="font-bold text-sm">Members Only</p>
          {posts.length > 0 && (
            <span className="text-[10px] bg-amber-500/10 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded-full font-semibold">
              {posts.length} post{posts.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {isOwn && (
          <button
            onClick={() => setComposing(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              composing ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary hover:bg-primary/20'
            }`}
          >
            <PenSquare className="w-3.5 h-3.5" />
            {composing ? 'Cancel' : 'New Post'}
          </button>
        )}
      </div>

      {/* Composer (creator only) */}
      {isOwn && composing && (
        <GatedPostComposer
          creatorPubkey={creatorPubkey}
          onDone={() => setComposing(false)}
        />
      )}

      {/* Post list */}
      <div className="divide-y divide-border">
        {loading && posts.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!loading && posts.length === 0 && !composing && (
          <div className="text-center py-8 px-4">
            <Lock className="w-8 h-8 mx-auto text-muted-foreground/20 mb-2" />
            <p className="text-sm text-muted-foreground">
              {isOwn ? 'No members-only posts yet. Create one above.' : 'No exclusive content yet.'}
            </p>
          </div>
        )}
        {posts.map(({ event, data }) => (
          <GatedPostItem key={event.id} event={event} data={data} isOwn={isOwn} />
        ))}
      </div>
    </div>
  );
}

function GatedPostComposer({ creatorPubkey, onDone }: { creatorPubkey: string; onDone: () => void }) {
  const { publish } = useNostrPublish();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const handlePublish = async () => {
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    try {
      const now = Math.floor(Date.now() / 1000);
      const dTag = GATED_POST_D_PREFIX + now;
      await publish({
        kind: GATED_POST_KIND,
        content: JSON.stringify({ title: title.trim(), body: body.trim(), createdAt: now }),
        tags: [
          ['d', dTag],
          ['t', GATED_POST_TAG],
          ['title', title.trim()],
        ],
        created_at: now,
      });
      onDone();
    } catch (e) {
      console.error('Gated post publish failed:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 space-y-3 border-b border-border bg-muted/20">
      <input
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="Post title…"
        className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground/40"
      />
      <textarea
        value={body}
        onChange={e => setBody(e.target.value)}
        placeholder="Write something exclusive for your members…"
        rows={5}
        className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground/40"
      />
      <div className="flex items-center gap-2 justify-end">
        <button
          onClick={onDone}
          className="px-4 py-2 rounded-xl text-sm text-muted-foreground hover:bg-muted transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handlePublish}
          disabled={saving || !title.trim() || !body.trim()}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Publish
        </button>
      </div>
    </div>
  );
}

function GatedPostItem({ event, data, isOwn }: {
  event: any; data: { title: string; body: string; createdAt: number }; isOwn: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const date = new Date(data.createdAt * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="p-4 space-y-2">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-start gap-3 text-left"
      >
        <FileText className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{data.title}</p>
          <p className="text-[11px] text-muted-foreground">{date}</p>
        </div>
        <ChevronRight className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && (
        <div className="pl-7 text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">
          {data.body}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  🔒 Gated Posts Teaser (non-members)
// ─────────────────────────────────────────────────────────────────

function GatedPostsTeaser({ creatorPubkey, membershipData }: {
  creatorPubkey: string; membershipData: MembershipData;
}) {
  const { posts } = useGatedPosts(creatorPubkey);
  if (posts.length === 0) return null;

  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Lock className="w-4 h-4 text-amber-500" />
        <p className="font-bold text-sm text-amber-700 dark:text-amber-400">
          {posts.length} exclusive post{posts.length !== 1 ? 's' : ''} for members
        </p>
      </div>
      {/* Blurred preview of post titles */}
      <div className="space-y-2">
        {posts.slice(0, 3).map(({ event, data }) => (
          <div key={event.id} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-background/60 border border-border/50">
            <Lock className="w-3 h-3 text-muted-foreground shrink-0" />
            <p className="text-xs text-muted-foreground blur-sm select-none flex-1 truncate">{data.title}</p>
          </div>
        ))}
        {posts.length > 3 && (
          <p className="text-[11px] text-muted-foreground text-center">+{posts.length - 3} more</p>
        )}
      </div>
      <p className="text-xs text-amber-700 dark:text-amber-400">
        Become a member for ${membershipData.priceUsd}/month to unlock all exclusive content.
      </p>
    </div>
  );
}

